import { useState } from 'react';

import useOrganization from '~/components/OrganizationContext';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  ClaimMilestoneDefinitionCreateJson,
  ClaimMilestoneDefinitionModel,
  ClaimMilestoneDefinitionsResponse,
  ClaimMilestoneDefinitionUpdateJson,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import {
  createClaimMilestoneDefinition,
  getClaimMilestoneDefinitionsUrl,
  reorderClaimMilestoneDefinitions,
  updateClaimMilestoneDefinition,
} from '../apiService';

// Stable while nothing is loaded: the screen resyncs its local order whenever this reference changes, so a
// fresh `[]` per render would resync (and re-render) forever.
const EMPTY_DEFINITIONS: ClaimMilestoneDefinitionModel[] = [];

const useClaimMilestoneDefinitionsAndMutations = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { organizationId } = useOrganization() as { organizationId: number };
  const { data, isLoading, isError, reloadData } = useDataFetcher<ClaimMilestoneDefinitionsResponse>(
    getClaimMilestoneDefinitionsUrl(organizationId)
  );

  const runMutation = async (mutation: () => Promise<unknown>) => {
    setIsUpdating(true);
    try {
      await mutation();
    } catch (error) {
      reportAxiosError(error);
      throw error;
    } finally {
      // Resync on both success and failure: the refetch is what reverts a failed reorder's optimistic order.
      // The saving flag is cleared even if the resync itself throws.
      try {
        await reloadData();
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const create = (values: ClaimMilestoneDefinitionCreateJson) =>
    runMutation(() => createClaimMilestoneDefinition(organizationId, values));

  const update = (milestoneKey: string, values: ClaimMilestoneDefinitionUpdateJson) =>
    runMutation(() => updateClaimMilestoneDefinition(organizationId, milestoneKey, values));

  const reorder = (milestoneKeys: string[]) =>
    runMutation(() => reorderClaimMilestoneDefinitions(organizationId, milestoneKeys));

  return {
    milestoneDefinitions: data?.milestone_definitions ?? EMPTY_DEFINITIONS,
    isLoading,
    isError,
    isUpdating,
    create,
    update,
    reorder,
  };
};

export default useClaimMilestoneDefinitionsAndMutations;
